import { useNavigate, useParams } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { PackagingType, validate as validateProductInventoryItem } from '../../models/ProductInventoryItem';
import { FormFieldValue } from '../forms/FormFieldValue';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_PRODUCT_BY_ID_QUERY, GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import ProductInventoryItemForm, { ProductInventoryItemFormState } from './ProductInventoryItemForm';
import { ADD_PRODUCT_INVENTORY_ITEM_MUTATION } from '../../mutations/ProductInventoryItemMutation';
import { GET_BATCHES_QUERY } from '../../queries/BatchQueries';

const INITIAL_FORM_VALUES: Partial<ProductInventoryItemFormState['values']> = {
  packagingType: PackagingType.JAR,
  quantityOnHand: 0,
  batchId: 0
};

export default function ProductInventoryItemCreate() {
  const navigate = useNavigate();
  const { productId } = useParams();

  const notifications = useNotifications();

  const { data: productData } = useQuery(
    GET_PRODUCT_BY_ID_QUERY, {
    variables: {
      id: +(productId || '0')
    }
  });

  const { data: batchesData } = useQuery(
    GET_BATCHES_QUERY, {
    variables: {
      filter: {
        productId: { eq: +(productId || '0') }
      }
    }
  });

  const [createProductInventoryItem] = useMutation(
    ADD_PRODUCT_INVENTORY_ITEM_MUTATION, {
    refetchQueries: [{ query: GET_PRODUCTS_QUERY }, { query: GET_PRODUCT_BY_ID_QUERY, variables: { id: productId } }],
  });

  const [formState, setFormState] = useState<ProductInventoryItemFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ProductInventoryItemFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ProductInventoryItemFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ProductInventoryItemFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ProductInventoryItemFormState['values']>) => {
        const { issues } = validateProductInventoryItem(values);

        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = useCallback(async (
    formValues: Partial<ProductInventoryItemFormState['values']>) => {
    const { issues } = validateProductInventoryItem(formValues);

    let errors = null;
    let hasErrors = false;

    if (issues && issues.length > 0) {
      errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors || {});
      return;
    }

    setFormErrors({});

    try {
      const { data: result } = await createProductInventoryItem({
        variables: {
          input: {
            id: 0,
            productId: +(productId || '0'),
            packagingType: formValues.packagingType || PackagingType.JAR,
            quantityOnHand: formValues.quantityOnHand || null,
            batchId: formValues.batchId || null
          }
        }
      });

      if (result?.added) {
        notifications.show('Product inventory item created successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate(`/dashboard/products/${productId}/product-inventory-items`);
      } else {
        notifications.show(
          'Failed to create product inventory item. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create product inventory item. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [createProductInventoryItem, navigate, notifications, productId, setFormErrors]);

  return (
    <PageContainer
      title="New Product Inventory"
      breadcrumbs={[
        { title: 'Products', path: '/dashboard/products' },
        { title: `${productData?.product?.name || 'N/A'}`, path: `/dashboard/products/${productId}` },
        { title: 'New Product Inventory' }
      ]}
    >
      <ProductInventoryItemForm
        productId={+(productId || '0')}
        batches={batchesData?.batches || []}
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
      />
    </PageContainer>
  );
}
